/**
 * Sentry Error Monitoring Configuration
 * 
 * This file contains all Sentry initialization and error filtering logic.
 * Moved from index.js for better code organization and maintainability.
 * 
 * Filter Strategy:
 * 1. Ghost Inspector automated testing tool errors
 * 2. Draft.js errors (handled gracefully by app)
 * 3. Network errors from analytics/extensions
 * 4. localStorage SecurityErrors (browser restrictions)
 * 5. Authentication cookie errors (expected session expiration)
 */

import * as Sentry from "@sentry/react";
import { SENTRY_REPO_URL } from '../constants/constants';

/**
 * Initialize Sentry error monitoring with comprehensive error filtering
 * @param {string} environment - 'production', 'stag', or other
 */
export function initializeSentry(environment) {
  Sentry.init({
    environment: environment === 'prod' ? 'production' : environment,
    dsn: SENTRY_REPO_URL,
    integrations: [
      new Sentry.BrowserTracing({
        // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
        tracePropagationTargets: ["app.resumebuilder.com", "stagingapp.resumetemplates.com"],
      }),
      // new Sentry.Replay({
      //   networkDetailAllowUrls: ['/wp-admin/admin-ajax.php','/api/*'],
      // }),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, // Capture 100% of the transactions, reduce in production!
    // Session Replay
    //replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    //replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

    // ==================================================================================
    // ERROR FILTERING STRATEGY
    // ==================================================================================
    // This beforeSend hook implements a multi-layer filtering strategy:
    //
    // 1. GHOST INSPECTOR DETECTION (Automated Testing Tool)
    //    - EXACT IP match: 52.4.55.112, 52.20.108.143, 52.201.131.218, 52.205.11.111
    //      (Ashburn, VA, AWS US-East-1) ← MOST RELIABLE
    //    - User-Agent string matching (if "Ghost Inspector" is present)
    //    - Browser signature fallback: Chrome + Linux + headless
    //
    // 2. DRAFT.JS ERROR FILTERING (For ALL Users)
    //    - Filters "Minified exception occurred" errors from Draft.js
    //    - Filters "Got unexpected null or undefined" nullthrows errors
    //    - These are handled gracefully by draftUtils.js and don't crash the app
    //    - Uses aggressive filename matching (not function names)
    //
    // 3. NETWORK ERROR FILTERING (For ALL Users)
    //    - Filters "Failed to fetch" errors from Google Tag Manager
    //    - Filters network errors from browser extensions
    //    - These are not actionable bugs
    //
    // 4. LOCALSTORAGE SECURITY ERRORS (For ALL Users)
    //    - Filters SecurityErrors when localStorage is blocked by browser
    //    - Common in Incognito mode, Android privacy settings
    //    - These are browser restrictions, not code bugs
    //
    // 5. AUTHENTICATION COOKIE ERRORS (For ALL Users)
    //    - Filters "Invalid authentication cookie" errors
    //    - These are expected session expiration errors (backend/WordPress)
    //    - Not a frontend bug - sessions expire naturally
    //    - App already handles this (redirects to login)
    //
    // 6. RECOMMENDED: Use Sentry's Inbound Filters (Server-Side)
    //    Navigate to: Sentry Project Settings > Inbound Filters
    //    Add filters for these IPs: 52.4.55.112, 52.20.108.143, 52.201.131.218, 52.205.11.111
    //    This filters events BEFORE they hit Sentry (saves quota)
    //    See: SENTRY_GHOST_INSPECTOR_FILTERING.md
    //
    // ==================================================================================
    beforeSend(event, hint) {
      const errorMessage = event?.message || event?.exception?.values?.[0]?.value || '';
      const stackTrace = event?.exception?.values?.[0]?.stacktrace?.frames || [];

      // ==================================================================================
      // GHOST INSPECTOR DETECTION (CLIENT-SIDE)
      // ==================================================================================
      if (isGhostInspectorError(event, hint, errorMessage)) {
        return null; // Don't send to Sentry
      }

      // ==================================================================================
      // DRAFT.JS ERROR FILTERING (FOR ALL USERS)
      // ==================================================================================
      if (isDraftJsError(errorMessage)) {
        return null; // Don't send to Sentry - already handled gracefully
      }

      // ==================================================================================
      // NETWORK ERROR FILTERING
      // ==================================================================================
      if (isFilteredNetworkError(errorMessage, stackTrace)) {
        return null; // Don't send to Sentry - not actionable
      }

      // ==================================================================================
      // LOCALSTORAGE SECURITY ERROR FILTERING
      // ==================================================================================
      if (isLocalStorageSecurityError(errorMessage, event)) {
        return null; // Don't send to Sentry - browser restriction
      }

      // ==================================================================================
      // AUTHENTICATION COOKIE ERROR FILTERING (RESUMEORG-REACT-8)
      // ==================================================================================
      if (isAuthenticationCookieError(errorMessage)) {
        return null; // Don't send to Sentry - expected session expiration
      }

      return event;
    },
  });
}

// ==================================================================================
// HELPER FUNCTIONS FOR ERROR FILTERING
// ==================================================================================

/**
 * Detect if error is from Ghost Inspector automated testing tool
 */
function isGhostInspectorError(event, hint, errorMessage) {
  const userAgent = (typeof navigator !== 'undefined' ? navigator.userAgent : '') || '';
  const browserName = event?.contexts?.browser?.name || '';
  const browserVersion = event?.contexts?.browser?.version || '';
  const osName = event?.contexts?.os?.name || '';

  // Ghost Inspector detection using client-side signals
  const isGhostInspector =
    // Method 1: Check for automation markers
    (typeof navigator !== 'undefined' && navigator.webdriver === true) ||
    // Method 2: HeadlessChrome in user agent
    userAgent.includes('HeadlessChrome') ||
    // Method 3: Specific Chrome version + Linux combo (Ghost Inspector signature)
    (browserName === 'Chrome' &&
      browserVersion?.startsWith('114') &&
      osName === 'Linux') ||
    // Method 4: Check for "Automated" or "Ghost Inspector" in UA
    userAgent.toLowerCase().includes('automated') ||
    userAgent.toLowerCase().includes('ghostinspector') ||
    userAgent.toLowerCase().includes('ghost inspector');

  // Debug log for Ghost Inspector detection
  if (isGhostInspector) {
    console.log('🤖 Sentry: Ghost Inspector detected (client-side)', {
      userAgent: userAgent.substring(0, 100),
      browserName,
      browserVersion,
      osName,
      webdriver: typeof navigator !== 'undefined' ? navigator.webdriver : 'N/A'
    });
  }

  // Filter out errors from Ghost Inspector
  if (isGhostInspector) {
    // Filter out QuotaExceededError from GhostInspector
    if (hint?.originalException?.name === 'QuotaExceededError' &&
      (event?.exception?.values?.[0]?.type === 'QuotaExceededError' ||
        errorMessage.includes('QuotaExceededError') ||
        errorMessage.includes('exceeded the quota'))) {
      console.log('🚫 Sentry: Filtering out QuotaExceededError from GhostInspector');
      return true;
    }

    // Filter out Draft.js errors from GhostInspector
    if (errorMessage.includes('Minified exception occurred')) {
      console.log('🚫 Sentry: Filtering out Draft.js error from GhostInspector');
      return true;
    }

    // Filter all other Ghost Inspector errors
    console.log('🚫 Sentry: Filtering out Ghost Inspector error');
    return true;
  }

  return false;
}

/**
 * Detect if error is from Draft.js (handled gracefully by app)
 */
function isDraftJsError(errorMessage) {
  // "Got unexpected null or undefined" is uniquely from fbjs/nullthrows, used only by Draft.js.
  // Stack frame check is skipped because beforeSend sees bundled filenames (pre-source-map),
  // so filename-based checks like 'draft-js' won't match in production builds.
  const isDraftNullthrows = errorMessage.includes('Got unexpected null or undefined');
  // "Minified exception occurred" is React/Draft.js internal — safe to filter on message alone.
  const isDraftMinified = errorMessage.includes('Minified exception occurred');

  // Fix: RESUMEORG-REACT-24, RESUMEORG-REACT-3X, RESUMEORG-REACT-2G/2F/2E - Filter Draft.js TypeErrors
  // These errors happen during typing/input and are handled gracefully by draftUtils.js.
  // Stack frame check removed: beforeSend sees bundled filenames, not source-mapped paths,
  // so 'draft-js' filename matching never works in production. The error message method names
  // (getBlockTree, getIn, etc.) are specific enough to Draft.js internals.
  const isDraftTypeError = (
    errorMessage.includes('undefined is not an object') ||
    errorMessage.includes('Cannot read properties of undefined') ||
    errorMessage.includes('Cannot read property') ||
    errorMessage.includes('null is not an object')
  ) && (
      errorMessage.includes('getBlockTree') ||
      errorMessage.includes('getIn') ||
      errorMessage.includes('getCharacterList') ||
      errorMessage.includes('getLength') ||
      errorMessage.includes('getType') ||
      errorMessage.includes('getText') ||
      errorMessage.includes('getInlineStyleAt') ||
      errorMessage.includes('textContent') ||
      errorMessage.includes('removeForBlock') ||
      errorMessage.includes('removeEntitiesAtEdges')
    );

  if (isDraftNullthrows) {
    console.log('🚫 Sentry: Filtering out Draft.js nullthrows error (handled by draftUtils)');
    return true;
  }

  if (isDraftMinified) {
    console.log('🚫 Sentry: Filtering out Draft.js minified error (handled by draftUtils)');
    return true;
  }

  if (isDraftTypeError) {
    console.log('🚫 Sentry: Filtering out Draft.js TypeError (handled by draftUtils)');
    return true;
  }

  return false;
}

/**
 * Detect if error is a network error from analytics or extensions
 * Issues: RESUMEORG-REACT-80, RESUMEORG-REACT-81
 */
function isFilteredNetworkError(errorMessage, stackTrace) {
  const isNetworkError = errorMessage.includes('Failed to fetch') ||
    errorMessage.includes('Network request failed') ||
    errorMessage.includes('NetworkError');

  if (isNetworkError) {
    // Check if error is from Google Tag Manager or Chrome extensions
    const hasGtagInStack = stackTrace.some(frame => {
      const filename = (frame?.filename || '').toLowerCase();
      return filename.includes('/gtag/js') ||
        filename.includes('chrome-extension://') ||
        filename.includes('gtm.js');
    });

    if (hasGtagInStack) {
      console.log('🚫 Sentry: Filtering out network error from analytics/extension', {
        error: errorMessage.substring(0, 50)
      });
      return true;
    }
  }

  return false;
}

/**
 * Detect if error is a localStorage SecurityError (browser restriction)
 * Issues: RESUMEORG-REACT-86, RESUMEORG-REACT-89
 */
function isLocalStorageSecurityError(errorMessage, event) {
  const isSecurityError =
    (errorMessage.includes('SecurityError') || errorMessage.includes('DOMException')) &&
    (errorMessage.includes('localStorage') || errorMessage.includes('storage'));

  if (isSecurityError) {
    console.log('🚫 Sentry: Filtering out localStorage SecurityError (browser restriction)', {
      error: errorMessage.substring(0, 100),
      browser: event?.contexts?.browser?.name,
      os: event?.contexts?.os?.name
    });
    return true;
  }

  return false;
}

/**
 * Detect if error is an authentication cookie error (backend/session issue)
 * Issue: RESUMEORG-REACT-8
 */
function isAuthenticationCookieError(errorMessage) {
  const isAuthError =
    errorMessage.includes('Invalid authentication cookie') ||
    errorMessage.includes('generate_auth_cookie');

  if (isAuthError) {
    console.log('🚫 Sentry: Filtering out authentication cookie error (expected session expiration)', {
      error: errorMessage.substring(0, 100)
    });
    return true;
  }

  return false;
}
